import { styled } from "styled-components"
import { Formation } from "../types"
import { PoolElement } from "../Pool"
import { darkBlueBackground, white } from './colors';
import { EditorButton } from "./EditorButton"
import {
  TrashIcon as RemoveFrameIcon,
  BackwardIcon as PreviousFrameIcon,
  ForwardIcon as NextFrameIcon,
  PlayIcon as StartPlayIcon,
  PauseIcon as StopPlayIcon,
  SquaresPlusIcon as AddFrameIcon,
  QuestionMarkCircleIcon as HelpIcon,
  CloudArrowUpIcon as SavePlayIcon,
} from "@heroicons/react/24/outline"
import useMeasure from "react-use-measure"
import { useCallback, useMemo, useState } from 'react';
import { SavePlayForm } from "./SavePlayForm"
import { $activeSnapshotIndex, $tactic } from '../store/tactic';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline"

const FrameListContainer = styled.div<{ $poolWidth: number }>`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  width: 66dvw;
  overflow: scroll;
  /* padding-left: ${(props) => props.$poolWidth}px;
  padding-right: ${(props) => props.$poolWidth}px; */
  box-sizing: content-box;
`

const Frame = styled.div<{ $selected?: boolean; $poolWidth: number }>`
  padding: ${(props) => props.$poolWidth / 12}px;
  border: ${(props) => (props.$selected ? `0.2rem solid ${white}` : "none")};
  border-radius: 1rem;
  cursor: pointer;
  position: relative;
`

const PlayEditorContainer = styled.div<{ $containerWidth: number }>`
  /* width: ${(props) => props.$containerWidth}px; */
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const PlayEditorControls = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 3rem;
  padding: 1rem 1rem 0rem 1rem; //no-bottom
  /* width: 100%; */
  box-sizing: border-box;
`

const PlayEditorControlButtonGroup = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 0.3rem;
  width: 100%;
`

const MoveArrowContainer = styled.div<{ direction: "left" | "right" }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 2rem;
  box-sizing: border-box;
  height: 2rem;
  border-radius: 0.3rem;
  background: #d1d1d1;
  cursor: pointer;
  position: absolute;
  bottom: 45%;
  z-index: 30;
  color: ${darkBlueBackground};
  transform: translateY(50%);
  ${(props) => props.direction === "left" && `left: -1rem;`};
  ${(props) => props.direction === "right" && `right: -1rem;`};
  padding: 0.2rem;
`

export enum EditorContent {
  CAP_NUMBERING,
  PLAY_EDITING,
}

export const PlayEditor: React.FC<{
  containerHeight: number
  containerWidth: number
  play: Formation
  selectedFrame: number
  isPlaying?: boolean
  onSelectFrame: (number) => void
  onRemoveFrame: (number) => void
  onAddFrame: () => void
  onStartPlay: () => void
  onStopPlay: () => void
  onNextFrame: () => void
  onPrevFrame: () => void
  onSubmitPlay: ({
    saveAsNew,
    name,
  }: {
    saveAsNew: boolean
    name?: string
  }) => void
  playNameList?: string[]
}> = ({
  containerHeight,
  containerWidth,
  play,
  selectedFrame,
  isPlaying,
  onSelectFrame,
  onRemoveFrame,
  onAddFrame,
  onStartPlay,
  onStopPlay,
  onNextFrame,
  onPrevFrame,
  onSubmitPlay,
  playNameList = [],
}) => {
  const [containerRef, { height: controlStripHeight }] = useMeasure()
  const poolMaxHeight = (containerHeight - controlStripHeight) * 0.66
  const poolMaxWidth = poolMaxHeight * 1.2

  // isSaving means we clicked the upload icon and we're seeing the save form
  const [isSaving, setIsSaving] = useState<boolean>(false)

  const isPlayable = useMemo(() => {
    console.log(' play?.snapshots?.length',  play?.snapshots?.length)
    return play?.snapshots?.length > 1
  }, [play])

  const onMoveLeft = useCallback(() => {
    let currentPlay = $tactic.get()
    const snapshots = currentPlay.snapshots
    if (snapshots?.length === 1) return
    const currentSnapshotIndex = selectedFrame;
    if (currentSnapshotIndex > 0) {
      const newSnapshots = snapshots.slice(0, currentSnapshotIndex - 1)
        .concat([snapshots[currentSnapshotIndex], snapshots[currentSnapshotIndex - 1]])
        .concat(snapshots.slice(currentSnapshotIndex + 1, snapshots.length))
      $tactic.setKey('snapshots', newSnapshots);
      setTimeout(() => {
        $activeSnapshotIndex.set(currentSnapshotIndex - 1);
      }, 1)
    }
  }, [selectedFrame]);

  const onMoveRight = useCallback(() => {
    let currentPlay = $tactic.get()
    const snapshots = currentPlay.snapshots
    if (snapshots?.length === 1) return
    const currentSnapshotIndex = selectedFrame;
    if (currentSnapshotIndex < snapshots.length - 1) {
      const newSnapshots = snapshots.slice(0, currentSnapshotIndex)
      .concat([snapshots[currentSnapshotIndex + 1], snapshots[currentSnapshotIndex]])
      .concat(snapshots.slice(currentSnapshotIndex + 2, snapshots.length))
      $tactic.setKey('snapshots', newSnapshots)
      setTimeout(() => {
        $activeSnapshotIndex.set(currentSnapshotIndex + 1);
      }, 1)
    }
  }, [selectedFrame])

  // const isPlayable = play?.snapshots?.length > 1

  return (
    <PlayEditorContainer $containerWidth={containerWidth}>
      {!isSaving && (
        <PlayEditorControls ref={containerRef}>
          <PlayEditorControlButtonGroup>
            <EditorButton
              onClick={onPrevFrame}
              icon={<PreviousFrameIcon />}
              disabled={!isPlayable}
            />
            <EditorButton
              onClick={() => {
                !isPlaying ? onStartPlay() : onStopPlay()
              }}
              icon={isPlaying ? <StopPlayIcon /> : <StartPlayIcon />}
              disabled={!isPlayable}
            />
            <EditorButton
              onClick={onNextFrame}
              icon={<NextFrameIcon />}
              disabled={!isPlayable}
            />
          </PlayEditorControlButtonGroup>
          <PlayEditorControlButtonGroup>
            <EditorButton
              onClick={() => onRemoveFrame(selectedFrame)}
              icon={<RemoveFrameIcon />}
              disabled={!isPlayable}
            />
            <EditorButton onClick={onAddFrame} icon={<AddFrameIcon />} />
          </PlayEditorControlButtonGroup>
          <PlayEditorControlButtonGroup>
            <EditorButton
              onClick={() => setIsSaving(!isSaving)}
              icon={<SavePlayIcon />}
              selected={isSaving}
            />
          </PlayEditorControlButtonGroup>
          <PlayEditorControlButtonGroup>
            <EditorButton icon={<HelpIcon />} noBackground />
          </PlayEditorControlButtonGroup>
        </PlayEditorControls>
      )}
      {!isSaving && play?.snapshots?.length && (
        <FrameListContainer $poolWidth={poolMaxWidth}>
          {play.snapshots.map((frame, index) => (
            <Frame
              $selected={index === selectedFrame}
              $poolWidth={poolMaxWidth}
              key={`editor-frame-${index}`}
              onClick={() => onSelectFrame(index)}
            >
              {play.snapshots.length > 1 && index === selectedFrame && (
                <>
                  {index !== 0 && (
                    <MoveArrowContainer direction={'left'}
                                        onClick={onMoveLeft}
                    >
                      <ChevronLeftIcon />
                    </MoveArrowContainer>
                  )}
                  {index !== play.snapshots.length - 1 && (
                    <MoveArrowContainer direction={'right'} onClick={onMoveRight}>
                      <ChevronRightIcon />
                    </MoveArrowContainer>
                  )}
                </>
              )}
              <PoolElement
                key={`play-editor-frame-${index}`}
                halfView={true}
                snapshot={frame}
                poolMaxHeight={poolMaxHeight}
                poolMaxWidth={poolMaxWidth}
                isDrawingEnabled={false}
                shouldCleanDrawings={false}
                onDrawingsCleaned={() => {}}
                shouldUndoLastDrawing={false}
                onLastDrawingUndone={() => {}}
                opponentIncrease={0}
                isStatic
                backgroundText={`${index + 1}`}
              />
            </Frame>
          ))}
        </FrameListContainer>
      )}
      {isSaving && (
        <SavePlayForm
          play={play}
          onSubmitPlay={onSubmitPlay}
          onCancel={() => {
            setIsSaving(false)
          }}
          playNameList={playNameList}
        />
      )}
    </PlayEditorContainer>
  )
}
